import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '@/integrations/supabase/client';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import {
  Building,
  PlusCircle,
  Edit,
  Trash2,
  Eye,
  ExternalLink,
  Upload,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUploadService } from '@/services/FileUploadService';
import { useAuth } from '@/contexts/AuthContext';
import { Business } from '@/types/business';
import { BusinessForm } from '@/components/business/BusinessForm';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface PlatformFeature {
  id: string;
  title: string;
  description: string;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

const ITEMS_PER_PAGE = 10;

export const BusinessManager = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active'>('all');

  // Query for total count with better stale time and caching
  const { data: totalCount = 0 } = useQuery({
    queryKey: ['businesses', 'count', statusFilter],
    queryFn: async () => {
      let query = supabase.from('businesses').select('*', { count: 'exact', head: true });
      
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { count } = await query;
      return count || 0;
    }
  });

  // Query for paginated businesses with optimized settings
  const { data: businesses = [], isLoading } = useQuery<Business[]>({
    queryKey: ['businesses', currentPage, statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('businesses')
        .select(`
          id,
          business_name,
          company_name,
          description,
          development_stage,
          status,
          created_at
        `)
        .order('created_at', { ascending: false });
        
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query
        .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

      if (error) throw error;
      return (data || []) as Business[];
    },
    placeholderData: (old) => old
  });

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  useEffect(() => {
    if (user) {
      fetchUserRole();
    }
  }, [user]);

  const fetchUserRole = async () => {
    if (!user) return;
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUserRole(profile.user_type);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const handleEdit = (business: Business) => {
    setSelectedBusiness(business);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;
    
    try {
      const { error } = await supabaseAdmin
        .from('businesses')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Invalidate and refetch queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['businesses'] }),
        queryClient.refetchQueries({ queryKey: ['businesses'] })
      ]);
      
      toast.success('Business deleted successfully');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to delete business');
    }
  };

  const handleSuccess = () => {
    setIsDialogOpen(false);
    // Invalidate and refetch queries
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      queryClient.refetchQueries({ queryKey: ['businesses'] })
    ]);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Business Management</h2>
        <div className="flex items-center gap-4">
          <Select
            value={statusFilter}
            onValueChange={(value: 'all' | 'draft' | 'active') => setStatusFilter(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setIsDialogOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Business
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Businesses</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business Name</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {businesses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4">
                      <p className="text-muted-foreground">No businesses found</p>
                      <Button variant="outline" className="mt-4" onClick={() => setIsDialogOpen(true)}>
                        Add your first business
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  businesses.map((business) => (
                    <TableRow key={business.id}>
                      <TableCell className="font-medium">
                        {business.business_name || business.company_name}
                      </TableCell>
                      <TableCell>{business.development_stage}</TableCell>
                      <TableCell>
                        <Badge variant={business.status === 'active' ? 'default' : 'secondary'}>
                          {business.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(business.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(business)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(business.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between space-x-2 py-4">
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedBusiness ? 'Edit Business' : 'Add New Business'}
            </DialogTitle>
            <DialogDescription>
              Fill in the business details below. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          
          <BusinessForm 
            business={selectedBusiness}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedBusiness(null);
            }}
            onSuccess={handleSuccess}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
